// Releases / Chromium / :major drill-in.
//
// Shrunk down to a header + breadcrumb + the tabbed shell. All real work
// happens inside the per-tab components under
// `components/chromium/`. Each tab keeps its own table to keep file sizes
// honest and let future tabs (e.g. fleet rollout view) slot in cleanly.

import { Button } from "@veil/ui/components/button";
import { formatRelative } from "@veil/ui/lib/format";
import { ChevronLeft, Globe, Plus } from "lucide-react";
import { useMemo } from "react";
import { Link } from "wouter";
import { ChromiumMajorTabs } from "@/components/chromium/major-tabs";
import { ProfileReleasesTable } from "@/components/chromium/profile-releases-table";
import { PublishedBuildsTable } from "@/components/chromium/published-builds-table";
import { UpstreamCatalogTable } from "@/components/chromium/upstream-catalog-table";
import { hasRole, useAuth } from "@/lib/auth-context";
import {
	useChromiumBuilds,
	useChromiumUpstream,
	useFleetStatus,
	useManifest,
	useProfileReleases,
} from "@/lib/releases/hooks.ts";

export function ChromiumMajorDetail({ major }: { major: string }) {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");
	const majorNum = Number.parseInt(major, 10);

	const manifestQuery = useManifest();
	const upstreamQuery = useChromiumUpstream();
	const fleetQuery = useFleetStatus();
	const profilesQuery = useProfileReleases(
		Number.isFinite(majorNum) ? majorNum : undefined,
	);
	const buildsQuery = useChromiumBuilds(
		Number.isFinite(majorNum) ? majorNum : undefined,
	);

	const majorEntry = manifestQuery.data?.manifest?.browserMajors?.[major];
	const buildsCount = useMemo(
		() => (majorEntry ? Object.keys(majorEntry.builds).length : 0),
		[majorEntry],
	);

	const publishedSet = useMemo(
		() => new Set(majorEntry ? Object.keys(majorEntry.builds) : []),
		[majorEntry],
	);

	const upstreamForMajor = useMemo(
		() =>
			(upstreamQuery.data?.releases ?? []).filter(
				(r) => r.browserMajor === majorNum && r.hasX64Deb,
			),
		[upstreamQuery.data, majorNum],
	);

	const fleetHosts = useMemo(
		() => fleetQuery.data?.hosts ?? [],
		[fleetQuery.data],
	);

	const profiles = profilesQuery.data?.releases ?? [];

	return (
		<div className="space-y-6">
			<div>
				<div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
					<Link href="/releases" className="hover:underline">
						Releases
					</Link>
					<span>›</span>
					<Link href="/releases/chromium" className="hover:underline">
						Chromium
					</Link>
					<span>›</span>
					<span>v{major}</span>
				</div>
				<div className="flex items-end justify-between gap-3">
					<div>
						<h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
							<Globe className="size-5" />
							Chromium v{major}
						</h1>
						<p className="text-sm text-muted-foreground">
							{buildsCount} build{buildsCount === 1 ? "" : "s"} ·{" "}
							{upstreamForMajor.length} upstream · {profiles.length} profile
							release
							{profiles.length === 1 ? "" : "s"}
							{upstreamQuery.data?.syncedAt
								? ` · catalog synced ${formatRelative(upstreamQuery.data.syncedAt)}`
								: " · catalog not yet synced"}
						</p>
					</div>
					{canPublish ? (
						<Button asChild>
							<Link href="/releases/new/chromium">
								<Plus className="mr-2 size-4" /> Publish chromium
							</Link>
						</Button>
					) : null}
				</div>
			</div>

			<ChromiumMajorTabs
				counts={{
					builds: buildsCount,
					upstream: upstreamForMajor.length,
					profiles: profiles.length,
				}}
				builds={
					<PublishedBuildsTable
						major={major}
						manifestMajor={majorEntry}
						loading={manifestQuery.isPending}
						fleetHosts={fleetHosts}
						builds={buildsQuery.data?.builds ?? []}
						canPublish={canPublish}
					/>
				}
				upstream={
					<UpstreamCatalogTable
						major={majorNum}
						releases={upstreamQuery.data?.releases ?? []}
						publishedSet={publishedSet}
						syncedAt={upstreamQuery.data?.syncedAt ?? null}
						loading={upstreamQuery.isPending}
						canPublish={canPublish}
					/>
				}
				profiles={
					<ProfileReleasesTable
						major={majorNum}
						releases={profiles}
						loading={profilesQuery.isPending}
						canPublish={canPublish}
						fleetHosts={fleetHosts}
					/>
				}
			/>

			<div>
				<Button asChild variant="ghost" size="sm">
					<Link href="/releases/chromium">
						<ChevronLeft className="mr-1 size-3.5" /> Back to Chromium
					</Link>
				</Button>
			</div>
		</div>
	);
}
